/**
 * Geometry Wars–style warp grid.
 * Fast path: straight lines when idle (no warps). Warped only during shocks.
 */
export class WarpGrid {
    constructor(options = {}) {
        this.cell = options.cell ?? 56;
        this.baseCell = this.cell;
        this.maxWarps = options.maxWarps ?? 6;
        this.warps = [];
        this.playerRipple = 0;
        this._out = { x: 0, y: 0 };
        this._quality = 'high';
        this._bombBoostFrames = 0;
        // Reused path scratch — avoid allocs
        this._tmp = { x: 0, y: 0 };
    }

    setQuality(q) {
        this._quality = q || 'high';
        if (q === 'low') {
            this.baseCell = 96;
            this.maxWarps = 3;
        } else if (q === 'medium') {
            this.baseCell = 72;
            this.maxWarps = 5;
        } else {
            this.baseCell = 56;
            this.maxWarps = 6;
        }
        this.cell = this.baseCell;
    }

    beginBombBoost(frames = 28) {
        this._bombBoostFrames = Math.max(this._bombBoostFrames, frames);
        if (this.warps.length > 2) {
            this.warps = this.warps
                .filter(w => w.kind === 'bomb' || w.strength >= 60)
                .slice(-2);
        }
    }

    pulse(x, y, kind = 'kill') {
        const presets = {
            kill:  { strength: 22,  speed: 18, life: 22, width: 28 },
            boss:  { strength: 48,  speed: 20, life: 32, width: 42 },
            bomb:  { strength: 90,  speed: 28, life: 28, width: 56 },
            hit:   { strength: 10,  speed: 16, life: 12, width: 18 },
        };
        const p = presets[kind] || presets.kill;

        if (this._bombBoostFrames > 0 && kind !== 'bomb' && kind !== 'boss') return;
        if (this._quality !== 'high' && kind === 'hit') return;
        if (this._quality === 'low' && kind === 'kill') return;

        if (kind === 'bomb') {
            for (let i = this.warps.length - 1; i >= 0; i--) {
                if (this.warps[i].kind === 'bomb') this.warps.splice(i, 1);
            }
        }

        this.warps.push({
            x, y,
            t: 0,
            life: p.life,
            strength: p.strength,
            speed: p.speed,
            width: p.width,
            widthSq: p.width * p.width,
            kind,
        });
        if (this.warps.length > this.maxWarps) {
            this.warps.splice(0, this.warps.length - this.maxWarps);
        }
    }

    update() {
        for (let i = this.warps.length - 1; i >= 0; i--) {
            this.warps[i].t++;
            if (this.warps[i].t >= this.warps[i].life) {
                this.warps.splice(i, 1);
            }
        }
        this.playerRipple += 0.08;
        if (this._bombBoostFrames > 0) this._bombBoostFrames--;
    }

    warpPoint(x, y, out = this._out) {
        let dx = 0;
        let dy = 0;
        const warps = this.warps;
        const n = warps.length;

        for (let i = 0; i < n; i++) {
            const w = warps[i];
            const ox = x - w.x;
            const oy = y - w.y;
            const distSq = ox * ox + oy * oy;
            const waveR = w.t * w.speed;
            const maxR = waveR + w.width * 2;
            if (distSq > maxR * maxR) continue;

            const dist = Math.sqrt(distSq) + 0.001;
            const front = (dist - waveR) / w.width;
            const ridge = Math.exp(-(front * front));
            let mag = w.strength * (1 - w.t / w.life) * ridge;
            if (dist < waveR) {
                mag -= w.strength * (1 - w.t / w.life) * 0.18 * (1 - dist / waveR);
            }
            const inv = 1 / dist;
            dx += ox * inv * mag;
            dy += oy * inv * mag;
        }

        out.x = x + dx;
        out.y = y + dy;
        return out;
    }

    draw(ctx, camera, viewW, viewH, player, accentColor = '#1a3a6e') {
        this.update();

        const boom = this._bombBoostFrames > 0;
        const hasWarps = this.warps.length > 0;
        const cell = boom ? Math.max(this.baseCell, 80) : this.baseCell;
        this.cell = cell;

        const pad = cell;
        const x0 = Math.floor((camera.x - pad) / cell) * cell;
        const y0 = Math.floor((camera.y - pad) / cell) * cell;
        const x1 = camera.x + viewW + pad;
        const y1 = camera.y + viewH + pad;

        ctx.save();
        ctx.lineJoin = 'miter';
        ctx.lineCap = 'butt';
        ctx.shadowBlur = 0; // never use shadowBlur on grid
        ctx.strokeStyle = hasWarps
            ? 'rgba(90, 220, 255, 0.36)'
            : 'rgba(70, 200, 255, 0.22)';
        ctx.lineWidth = hasWarps ? 1.2 : 1;

        if (!hasWarps) {
            // ── FAST PATH: straight grid, no warp math ──
            // One path for all verticals, one for all horizontals
            ctx.beginPath();
            for (let x = x0; x <= x1; x += cell) {
                ctx.moveTo(x, y0);
                ctx.lineTo(x, y1);
            }
            ctx.stroke();

            ctx.beginPath();
            for (let y = y0; y <= y1; y += cell) {
                ctx.moveTo(x0, y);
                ctx.lineTo(x1, y);
            }
            ctx.stroke();
        } else {
            // ── WARP PATH: sample along lines (no subdivide) ──
            const step = cell; // one sample per cell — cheap
            const tmp = this._tmp;

            for (let x = x0; x <= x1; x += cell) {
                ctx.beginPath();
                let first = true;
                for (let y = y0; y <= y1; y += step) {
                    const p = this.warpPoint(x, y, tmp);
                    if (first) { ctx.moveTo(p.x, p.y); first = false; }
                    else ctx.lineTo(p.x, p.y);
                }
                ctx.stroke();
            }

            for (let y = y0; y <= y1; y += cell) {
                ctx.beginPath();
                let first = true;
                for (let x = x0; x <= x1; x += step) {
                    const p = this.warpPoint(x, y, tmp);
                    if (first) { ctx.moveTo(p.x, p.y); first = false; }
                    else ctx.lineTo(p.x, p.y);
                }
                ctx.stroke();
            }

            // Shock rings (simple arcs)
            ctx.lineWidth = 1.5;
            for (let wi = 0; wi < this.warps.length; wi++) {
                const w = this.warps[wi];
                const decay = 1 - w.t / w.life;
                const r = w.t * w.speed;
                if (r < 2) continue;
                if (w.x + r < camera.x - 20 || w.x - r > camera.x + viewW + 20) continue;
                if (w.y + r < camera.y - 20 || w.y - r > camera.y + viewH + 20) continue;

                ctx.globalAlpha = 0.22 + decay * 0.4;
                ctx.strokeStyle = w.kind === 'bomb' || w.strength > 70 ? '#ff66aa' : '#66eeff';
                ctx.beginPath();
                ctx.arc(w.x, w.y, r, 0, Math.PI * 2);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }

        ctx.restore();
    }
}
